import { memo } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { Check } from '@/lib/websocket'
import type { ProjectGroup } from '@/hooks/useChecks'
import type { QuickTestResult } from '@/hooks/useCheckQuickTest'
import { QuickTestTile } from '@/components/QuickTestButton'
import { cn } from '@/lib/utils'

interface HealthMapProps {
  groups: ProjectGroup[]
  onSelectCheck: (uuid: string) => void
  /** Quick-test callbacks. When present, tiles show a test overlay on hover. */
  onQuickTest?: (uuid: string) => void
  getQuickTestState?: (uuid: string) => QuickTestResult
  isQuickTestDisabled?: (uuid: string) => boolean
  getQuickTestCooldownLabel?: (uuid: string) => string | null
}

function truncateName(name: string, maxChars = 9): string {
  if (name.length <= maxChars) return name
  return name.slice(0, maxChars - 1) + '…'
}

function getFailureDuration(lastExec: string): string {
  if (!lastExec) return ''
  const date = new Date(lastExec)
  if (isNaN(date.getTime())) return ''
  const diffMs = Date.now() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return '<1m'
  if (diffMins < 60) return `${diffMins}m`
  const hours = Math.floor(diffMins / 60)
  if (hours < 24) {
    const mins = diffMins % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }
  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`
}

const HealthTile = memo(function HealthTile({
  check,
  onSelect,
  onQuickTest,
  quickTestState,
  quickTestDisabled,
  quickTestCooldownLabel,
}: {
  check: Check
  onSelect: () => void
  onQuickTest?: (uuid: string) => void
  quickTestState?: QuickTestResult
  quickTestDisabled?: boolean
  quickTestCooldownLabel?: string | null
}) {
  const hasPartialSilence = !check.IsSilenced && check.SilencedChannels && check.SilencedChannels.length > 0

  const status = !check.Enabled
    ? 'disabled'
    : check.IsSilenced
      ? 'silenced'
      : hasPartialSilence
        ? 'partial'
        : check.LastResult
          ? 'healthy'
          : 'unhealthy'

  const isUnhealthy = status === 'unhealthy'
  const failureDuration = isUnhealthy ? getFailureDuration(check.LastExec) : ''

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={cn(
            'rounded-sm transition-all cursor-pointer border relative group/tile',
            'hover:scale-110 hover:z-10',
            // Failing tiles are slightly larger than healthy ones
            isUnhealthy
              ? 'h-12 w-12 sm:h-10 sm:w-10'
              : 'h-10 w-10 sm:h-8 sm:w-8',
            status === 'healthy' && 'bg-healthy/80 border-healthy/40 hover:bg-healthy',
            isUnhealthy && [
              'bg-unhealthy/80 border-unhealthy/40 hover:bg-unhealthy animate-pulse-unhealthy',
              'shadow-[0_0_8px_2px_rgba(239,68,68,0.4)]',
            ],
            status === 'disabled' && 'bg-disabled/30 border-disabled/20 hover:bg-disabled/50',
            status === 'silenced' && 'bg-warning/40 border-warning/30 hover:bg-warning/60',
            status === 'partial' && 'bg-warning/25 border-warning/20 hover:bg-warning/40'
          )}
          onClick={onSelect}
        >
          {isUnhealthy && (
            <span className="absolute inset-x-0 bottom-0.5 text-center text-[9px] text-white/90 truncate px-0.5 leading-none pointer-events-none select-none">
              {truncateName(check.Name)}
            </span>
          )}
          {onQuickTest && quickTestState && (
            <QuickTestTile
              uuid={check.UUID}
              state={quickTestState}
              disabled={quickTestDisabled ?? false}
              cooldownLabel={quickTestCooldownLabel ?? null}
              onTest={onQuickTest}
            />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[300px]">
        <div className="space-y-1">
          <div className="font-medium text-xs">{check.Name}</div>
          <div className="text-[10px] text-muted-foreground">
            {check.CheckType} · {check.URL || check.Host || '—'}
          </div>
          <div
            className={cn(
              'text-[10px] font-mono',
              status === 'healthy' && 'text-healthy',
              status === 'unhealthy' && 'text-unhealthy',
              status === 'disabled' && 'text-disabled',
              (status === 'silenced' || status === 'partial') && 'text-warning'
            )}
          >
            {status === 'healthy'
              ? 'Healthy'
              : status === 'unhealthy'
                ? 'FAILING'
                : status === 'disabled'
                  ? 'Disabled'
                  : status === 'partial'
                    ? `Partially silenced (${check.SilencedChannels?.join(', ')})`
                    : 'Silenced'}
            {isUnhealthy && failureDuration && (
              <span className="text-muted-foreground ml-1 font-normal">· failing for {failureDuration}</span>
            )}
          </div>
          {check.Message && isUnhealthy && (
            <div className="text-[10px] text-unhealthy/80 truncate">{check.Message}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
})

export function HealthMap({
  groups,
  onSelectCheck,
  onQuickTest,
  getQuickTestState,
  isQuickTestDisabled,
  getQuickTestCooldownLabel,
}: HealthMapProps) {
  if (groups.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">
        No checks found
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const hasFailing = group.failingCount > 0
        const failingChecks = group.checks.filter((c) => c.Enabled && !c.LastResult)

        return (
          <div
            key={group.name}
            className={cn(
              'rounded-lg border bg-card p-3 transition-colors',
              hasFailing && 'border-unhealthy/40 bg-unhealthy/5'
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-sm text-foreground">{group.name}</span>
              <span className="text-xs text-muted-foreground">
                {group.checks.length} check{group.checks.length !== 1 ? 's' : ''}
              </span>
              {hasFailing && (
                <span className="text-[10px] text-unhealthy font-medium">
                  {group.failingCount} failing
                </span>
              )}
            </div>
            {hasFailing && (
              <div className="mb-2 space-y-0.5">
                {failingChecks.map((check) => {
                  const duration = getFailureDuration(check.LastExec)
                  return (
                    <div key={check.UUID} className="text-[10px] text-unhealthy leading-snug truncate">
                      <span className="mr-0.5">⚠</span>
                      <span className="font-medium">{check.Name}</span>
                      {check.Message && (
                        <span className="text-unhealthy/70"> — {check.Message}</span>
                      )}
                      {duration && (
                        <span className="text-unhealthy/60"> — {duration}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 items-center">
              {group.checks.map((check) => (
                <HealthTile
                  key={check.UUID}
                  check={check}
                  onSelect={() => onSelectCheck(check.UUID)}
                  onQuickTest={onQuickTest}
                  quickTestState={getQuickTestState?.(check.UUID)}
                  quickTestDisabled={isQuickTestDisabled?.(check.UUID)}
                  quickTestCooldownLabel={getQuickTestCooldownLabel?.(check.UUID)}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
